import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, asAttachments, type TicketDetail, type TicketReply } from '../lib/api';
import { useAuth } from '../lib/auth';
import { priorityLabel, statusLabel, useI18n } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { clientPersonName, companyName } from '../lib/people';
import { AttachmentComposer, AttachmentList, DropZone, useUploads } from '../components/Attachments';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  ErrorState,
  LoadingBlock,
  PageHeader,
  Textarea,
  priorityTone,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * One ticket: its details, our replies (never an internal note — the API sends
 * none), the resolution, and the client's answer box while the ticket is open.
 */
export function TicketDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { account } = useAuth();
  const company = companyName(account, locale);

  const { data: ticket, isLoading, error, refetch } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.get<TicketDetail>(`/tickets/${id}`),
    // A reply from us shows without a reload.
    refetchInterval: 60_000,
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !ticket) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div className="mb-4">
        <Link to="/tickets" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('tickets.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{ticket.number}</span>
            <span>{ticket.subject}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(ticket.status)}>{statusLabel(t, 'ticket', ticket.status)}</Badge>
            <Badge tone={priorityTone(ticket.priority)}>{priorityLabel(t, ticket.priority)}</Badge>
            <span className="text-muted">{ticket.project ? localized(ticket.project) : t('common.noProject')}</span>
          </span>
        }
      />

      {ticket.status === 'WAITING_REQUESTER' && (
        <Card className="mb-4 border-warning/40">
          <p className="text-body-sm font-medium text-warning-fg dark:text-warning-dark">{t('tickets.waitingOnYou')}</p>
        </Card>
      )}

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="min-w-0 space-y-5 xl:col-span-2">
          {ticket.resolution && (
            <Card className="border-success/30">
              <p className="mb-1 text-caption uppercase text-success-fg dark:text-success">{t('tickets.resolution')}</p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {ticket.resolution}
              </p>
            </Card>
          )}
          <Conversation ticket={ticket} company={company} />
        </div>

        <div className="min-w-0 space-y-5">
          <Card>
            <CardHeader title={t('tickets.details')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('common.status')}>{statusLabel(t, 'ticket', ticket.status)}</Detail>
              <Detail label={t('tickets.handledBy')}>
                {ticket.handledBy ? localized(ticket.handledBy) : t('tickets.notAssigned')}
              </Detail>
              <Detail label={t('common.project')}>
                {ticket.project ? localized(ticket.project) : t('common.noProject')}
              </Detail>
              {ticket.department && <Detail label={t('tickets.department')}>{localized(ticket.department)}</Detail>}
              <Detail label={t('common.created')}>{formatDateTime(ticket.createdAt, locale)}</Detail>
              <Detail label={t('tickets.raisedBy')}>
                {ticket.raisedByClient
                  ? ticket.raisedBy
                    ? clientPersonName(ticket.raisedBy, company, t('common.you'))
                    : t('common.you')
                  : t('common.viganium')}
              </Detail>
              {ticket.requestedBy && (
                <Detail label={t('tickets.requestedBy')}>
                  {ticket.requestedBy.jobTitle
                    ? `${ticket.requestedBy.name} — ${ticket.requestedBy.jobTitle}`
                    : ticket.requestedBy.name}
                </Detail>
              )}
              {ticket.resolvedAt && (
                <Detail label={statusLabel(t, 'ticket', 'RESOLVED')}>{formatDateTime(ticket.resolvedAt, locale)}</Detail>
              )}
            </dl>
            {ticket.description && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('tickets.description')}</p>
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {ticket.description}
                </p>
              </div>
            )}
            {ticket.attachments.length > 0 && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">{t('tickets.files')}</p>
                <AttachmentList attachments={asAttachments(ticket.attachments)} />
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

function Conversation({ ticket, company }: { ticket: TicketDetail; company: string }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const uploads = useUploads();
  const canPost = (body.trim() !== '' || uploads.fileIds.length > 0) && !uploads.busy;

  const post = useMutation({
    mutationFn: () =>
      api.post<TicketReply>(`/tickets/${ticket.id}/replies`, {
        body: body.trim() || undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: () => {
      setBody('');
      uploads.clear();
      toast.success(t('tickets.sent'));
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader
        title={t('tickets.conversation')}
        action={<Badge tone="neutral" className="tabular">{ticket.replies.length}</Badge>}
      />

      {ticket.replies.length ? (
        <ul className="mb-4 space-y-3">
          {ticket.replies.map((reply) => (
            <li
              key={reply.id}
              className={
                'rounded-md border p-3 ' +
                (reply.fromClient ? 'border-primary/25 bg-primary/6 ms-6' : 'border-[var(--line)] me-6')
              }
            >
              <div className="mb-1 flex flex-wrap items-baseline gap-2">
                <span className="text-body-sm font-medium text-[var(--text-primary)]">
                  {reply.fromClient
                    ? reply.clientAuthor
                      ? clientPersonName(reply.clientAuthor, company, t('common.you'))
                      : t('common.you')
                    : reply.author
                      ? localized(reply.author)
                      : t('common.viganium')}
                </span>
                {!reply.fromClient && reply.author?.jobTitle && (
                  <span className="text-caption text-muted">{reply.author.jobTitle}</span>
                )}
                {/* A colleague at the client: their job title, as ours show theirs. */}
                {reply.fromClient && reply.clientAuthor && !reply.clientAuthor.you && reply.clientAuthor.jobTitle && (
                  <span className="text-caption text-muted">{reply.clientAuthor.jobTitle}</span>
                )}
                {!reply.fromClient && <Badge tone="brand">{t('common.viganium')}</Badge>}
                <span className="text-caption text-faint">{formatDateTime(reply.createdAt, locale)}</span>
              </div>
              {reply.body && (
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {reply.body}
                </p>
              )}
              <AttachmentList attachments={asAttachments(reply.attachments)} className={reply.body ? 'mt-2' : undefined} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-body-sm text-muted">{t('tickets.noReplies')}</p>
      )}

      {ticket.canReply ? (
        <DropZone uploads={uploads}>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onPaste={uploads.onPaste}
            placeholder={t('tickets.answerPlaceholder')}
            aria-label={t('tickets.answer')}
            className="min-h-[90px]"
          />
          <AttachmentComposer
            uploads={uploads}
            className="mt-2"
            actions={
              <Button
                variant="primary"
                size="sm"
                icon={<Send className="h-4 w-4 flip-rtl" />}
                disabled={!canPost}
                loading={post.isPending}
                onClick={() => post.mutate()}
              >
                {t('common.send')}
              </Button>
            }
          />
        </DropZone>
      ) : (
        <p className="rounded-md border hairline bg-[var(--surface-sunken)] px-3 py-2 text-body-sm text-muted">
          {t('tickets.finished')}
        </p>
      )}
    </Card>
  );
}
